import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  MenuItem,
  styled,
  GlobalStyles,
} from "@mui/material";
import DialogHeader from "app/components/common/DialogHeader";
import { CustomSelectBox } from "assets/styles/globalStyledComponent";
import TransportCardTemplate1 from "./TransportCardTemplate1";
import TransportCardTemplate2 from "./TransportCardTemplate2";

export default function TransportCardModal({
  open,
  onClose,
  filteredData = [],
  printRef,
  handlePrint,
  selectedSession,
  template = 1,
  setTemplate,
  color = "#e6ffe9",
  setColor,
}) {
  // logic to render multiple cards per printed page
  const CARDS_PER_PAGE = template === 1 ? 4 : 6; // cards per page;
  const chunkArray = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size)
      chunks.push(arr.slice(i, i + size));
    return chunks;
  };

  const pages = chunkArray(filteredData || [], CARDS_PER_PAGE);

  return (
    <>
      <GlobalStyles
        styles={{
          "@page": {
            size: "auto",
            margin: "5mm",
          },
          "@media print": {
            "html, body": {
              margin: 0,
              padding: 0,
              height: "100%",
              width: "100%",
            },
            ".print-area": {
              margin: 0,
              padding: 0,
              boxSizing: "border-box",
            },
            ".MuiDialog-paper": {
              boxShadow: "none",
              borderRadius: 0,
              margin: 0,
            },
            /* each page has top padding and breaks between pages */
            ".print-page": {
              pageBreakAfter: "always",
              breakAfter: "page",
              paddingTop: "5mm", // ensures consistent top space on every printed page
              boxSizing: "border-box",
            },
            /* do not insert extra blank page after last page */
            ".print-page:last-child": {
              pageBreakAfter: "auto",
              breakAfter: "auto",
            },
          },
        }}
      />

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minWidth: "760px", minHeight: "520px" },
        }}
      >
        <DialogHeader title="Print Transport Card" handleClose={onClose} />

        {/* Template and Color Selection Controls */}
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e0e0e0" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Stack direction="row" gap={2} alignItems="center">
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Select Template :
              </Typography>
              <CustomSelectBox
                label="Select Template"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
              >
                <MenuItem value={1}>Template 1</MenuItem>
                <MenuItem value={2}>Template 2</MenuItem>
              </CustomSelectBox>
            </Stack>

            <Stack direction="row" gap={1.5} alignItems="center">
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Change Color :
              </Typography>
              <ColorBox
                sx={{
                  backgroundColor: "transparent",
                  transform: color === "#fff" && "scale(1.2)",
                  border:
                    color === "#fff" ? "2px solid #2196f3" : "1px solid gray",
                }}
                onClick={() => setColor("#fff")}
              />
              <ColorBox
                sx={{
                  backgroundColor: "#e6ffe9",
                  transform: color === "#e6ffe9" && "scale(1.2)",
                  border:
                    color === "#e6ffe9"
                      ? "2px solid #2196f3"
                      : "1px solid gray",
                }}
                onClick={() => setColor("#e6ffe9")}
              />
              <ColorBox
                sx={{
                  backgroundColor: "#fff5b9",
                  transform: color === "#fff5b9" && "scale(1.2)",
                  border:
                    color === "#fff5b9"
                      ? "2px solid #2196f3"
                      : "1px solid gray",
                }}
                onClick={() => setColor("#fff5b9")}
              />
            </Stack>
          </Stack>
        </Box>

        <DialogContent>
          <div ref={printRef} className="print-area">
            <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
              {filteredData.length === 0 && (
                <Typography
                  sx={{ textAlign: "center", py: 6, color: "text.secondary" }}
                >
                  No students selected
                </Typography>
              )}

              {pages.map((page, pageIndex) => (
                <Box
                  key={`page-${pageIndex}`}
                  className="print-page"
                  sx={{ width: "100%", display: "block" }}
                >
                  {page.map((student, index) =>
                    template === 1 ? (
                      <TransportCardTemplate1
                        key={student?._id || `${pageIndex}-${index}`}
                        student={student}
                        index={index}
                        pageIndex={pageIndex}
                        color={color}
                        selectedSession={selectedSession}
                      />
                    ) : (
                      <TransportCardTemplate2
                        key={student?._id || `${pageIndex}-${index}`}
                        student={student}
                        index={index}
                        pageIndex={pageIndex}
                        color={color}
                        selectedSession={selectedSession}
                      />
                    ),
                  )}
                </Box>
              ))}
            </Box>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Close
          </Button>
          <Button onClick={handlePrint} variant="contained" color="primary">
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const ColorBox = styled(Box)`
  height: 25px;
  width: 25px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow:
    rgba(50, 50, 93, 0.25) 0px 2px 5px -1px,
    rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;
